import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';

import { ModalHeader } from 'shared/ModalHeader';
import { getActionDisplayText } from 'modules/automation/utils/getActionDisplayText';
import { convertToSpecificTimeZoneFormat } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import useGetStoppedAutomation from 'modules/automation/hooks/useGetStoppedAutomation';
import './ViewInstanceStatus.css';

const getActionStatus = (action) => {
  if (action?.status === 'error') {
    return 'error';
  }
  if (action?.status === 'completed') {
    return 'completed';
  }
  if (action?.status === 'stopped') {
    return 'stopped';
  }
  return 'running';
};

const getStatusIcon = (status) => {
  const icons = {
    completed: (
      <div className="automation-timeline-icon automation-timeline-icon-success">
        <i className="pi pi-check white-icon" />
      </div>
    ),
    stopped: (
      <div className="automation-timeline-icon automation-timeline-icon-error">
        <i className="pi pi-exclamation-triangle white-icon" />
      </div>
    ),
    error: (
      <div className="automation-timeline-icon automation-timeline-icon-danger">
        <i className="pi pi-times white-icon" />
      </div>
    ),
    running: (
      <div className="automation-timeline-icon automation-timeline-icon-running">
        <i className="pi pi-spin pi-spinner white-icon" />
      </div>
    ),
  };
  return icons[status] || null;
};

const getStatusBadge = (status) => {
  const badges = {
    completed: <span className="automation-timeline-badge automation-timeline-badge-success">Completed</span>,
    stopped: <span className="automation-timeline-badge automation-timeline-badge-error">Stopped</span>,
    error: <span className="automation-timeline-badge automation-timeline-badge-danger">Error</span>,
    running: <span className="automation-timeline-badge automation-timeline-badge-running">Running</span>,
  };
  return badges[status] || null;
};

const getTimelineContentStyle = (status) => {
  const styles = {
    completed: { backgroundColor: 'var(--status-completed-bg)', border: '1px solid var(--status-completed-border)' },
    stopped: { backgroundColor: 'var(--status-stopped-bg)', border: '1px solid var(--status-stopped-border)' },
    error: { backgroundColor: 'var(--status-error-bg)', border: '1px solid var(--status-error-border)' },
    running: { backgroundColor: 'var(--status-running-bg)', border: '1px solid var(--status-running-border)' },
  };
  return { ...styles[status], borderRadius: '8px' };
};

const ViewInstanceStatus = ({ onHide, instanceData }) => {
  const { stoppedAutomationData } = useGetStoppedAutomation(instanceData?.instance_id);
  const { userDetails } = useUserDetailsContext();

  const isLoading = stoppedAutomationData === null;

  return (
    <Dialog header={ModalHeader} visible onHide={onHide} style={{ width: '60vw' }} modal closable={false}>
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <div>
          <div className="modal-heading F-size16 text-bold">Automation Instance Status</div>
          <div className="text-muted F-size12 mt-1">View the execution timeline of automation actions</div>
        </div>
      </div>

      <div className="p-fluid">
        <div className="mb-4">
          {/* Instance Info */}
          <div className="mb-4 p-3 card-bg automation-instance-info-card">
            <div className="row">
              <div className="col-4">
                <div className="text-muted small">Case/Lead Name</div>
                <div className="text-bold">{instanceData?.case_name || 'N/A'}</div>
              </div>
              <div className="col-4">
                <div className="text-muted small">Triggered At</div>
                <div className="text-bold">{convertToSpecificTimeZoneFormat(instanceData?.created_at, userDetails?.timezone) || 'N/A'}</div>
              </div>
              <div className="col-4">
                <div className="text-muted small">Instance Stopped Time</div>
                <div className="text-bold">
                  {convertToSpecificTimeZoneFormat(instanceData?.instance_stop_time, userDetails?.timezone) || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          <div className="automation-timeline-container overflow-y-auto pe-2" style={{ maxHeight: '340px' }}>
            {isLoading && (
              <div className="text-center py-4">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                <div className="text-muted mt-2">Loading automation actions...</div>
              </div>
            )}

            {!isLoading &&
              stoppedAutomationData
                ?.sort((a, b) => {
                  const timeA = new Date(a?.scheduled_time || 0).getTime();
                  const timeB = new Date(b?.scheduled_time || 0).getTime();
                  return timeA - timeB;
                })
                ?.map((action, index) => {
                  const status = getActionStatus(action);
                  const isLast = index === stoppedAutomationData.length - 1;
                  const actionId = action?.execute_link?.action_code || `action-${index}`;

                  let lineColor = 'var(--black-400'; // default gray for running
                  if (status === 'completed') {
                    lineColor = '#28a745'; // green for completed
                  } else if (status === 'error') {
                    lineColor = '#dc3545'; // red for error
                  }

                  const actionLabel = action?.execute_link?.action_label || 'Unknown Action';

                  return (
                    <div
                      key={`${actionId}-${index}`}
                      className={`automation-timeline-item ${status}`}
                      style={{ position: 'relative', paddingLeft: '50px', paddingBottom: isLast ? '0' : '30px' }}
                    >
                      {/* Timeline Line */}
                      {!isLast && (
                        <div
                          className="automation-timeline-line"
                          style={{
                            height: 'calc(100% - 10px)',
                            backgroundColor: lineColor,
                          }}
                        />
                      )}

                      {/* Icon */}
                      <div style={{ position: 'absolute', left: '0', top: '0' }}>{getStatusIcon(status)}</div>

                      <div className="automation-timeline-content p-3" style={getTimelineContentStyle(status)}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <span className="text-bold F-size14" style={{ marginRight: '10px' }}>
                                {actionLabel}
                              </span>
                              {getStatusBadge(status)}
                            </div>
                            {getActionDisplayText(action?.execute_link)}

                            {/* Timing Information */}
                            <div className="mt-2">
                              {action?.scheduled_time && (
                                <div className="text-muted small mb-1">
                                  <i className="pi pi-clock me-2" style={{ fontSize: '12px' }} />
                                  <strong>Scheduled:</strong>{' '}
                                  {convertToSpecificTimeZoneFormat(action.scheduled_time, userDetails?.timezone)}
                                </div>
                              )}
                              {action?.error_time && status === 'error' && (
                                <div className="text-muted small mb-1">
                                  <i className="pi pi-times-circle me-1" style={{ fontSize: '10px' }} />
                                  <strong>Error:</strong> {convertToSpecificTimeZoneFormat(action.error_time, userDetails?.timezone)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

            {!isLoading && stoppedAutomationData?.length === 0 && (
              <div className="text-center text-muted py-4">No actions available for this automation instance</div>
            )}
          </div>
        </div>
      </div>

      <div className="footer-btn">
        <div className="col-12 d-flex justify-content-end">
          <Button label="Close" className="p-button-secondary" onClick={onHide} />
        </div>
      </div>
    </Dialog>
  );
};

ViewInstanceStatus.propTypes = {
  onHide: PropTypes.func.isRequired,
  instanceData: PropTypes.shape({
    case_name: PropTypes.string,
    instance_stop_time: PropTypes.string,
    instance_id: PropTypes.string,
    stopped_at_index: PropTypes.number,
    current_index: PropTypes.number,
    status: PropTypes.string,
  }).isRequired,
};

export default ViewInstanceStatus;
